import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { parse } from 'date-fns';
import cn from 'classnames';

import { useTheme } from '../../../theme';
import { Props as LocationProps } from '../../../Location';
import { format, removeDotsFromDate } from '../../../utils';
import { weatherIcons } from '../../../Weather/icons';
import { getTemperaturePrefix } from '../../../Weather/utils';
import { TemperatureUnits } from '@websky/graphql';
import { useConfig } from '../../../context';
import { CdnIcon, CdnIconNames } from '@shared/ui';

const FLIGHT_ICON_MAP: Record<'arrival' | 'departure', CdnIconNames> = {
	arrival: 'Flight/Land',
	departure: 'Flight/Takeoff'
};

interface Props extends LocationProps {
	className?: string;
	isMobile?: boolean;
}

const Location: React.FC<Props> = ({ className, airport, date, time, weather, type, isMobile, terminal }) => {
	const { Location: css } = useTheme('DesktopFlight');
	const { t } = useTranslation('Location');

	const flightDate = parse(date, 'yyyy-MM-dd', new Date());
	const weatherIcon = weather?.type && weatherIcons[weather.type];
	const image = airport?.city?.images?.panorama?.url ?? airport.images?.panorama?.url;

	const showCitiesPlaceholder = useConfig().global.showCitiesPlaceholder;
	const titleEqualsAirportName = airport.city?.name === airport.title;

	const flightIconByType = FLIGHT_ICON_MAP[type];
	const flightIcon = <CdnIcon icon={flightIconByType} />;

	return (
		<div
			className={cn(className, css.wrapper, {
				[css.isMobile]: isMobile
			})}
		>
			<div className={css.info}>
				<span className={css.time}>
					{time}
					<span className={css.time_iata}>{airport.iata?.toUpperCase()}</span>
				</span>
				<span className={css.date}>
					<div className={css.date_icon}>{flightIcon}</div>
					{removeDotsFromDate(format(flightDate, 'dd MMM, EEEEEE'))}
				</span>
			</div>

			<div className={css.airport}>
				<span className={css.city}>{airport.city?.name ? airport.city?.name : airport.title}</span>
				<span className={css.terminal}>
					<span className={css.terminal_city}>{airport.city?.name}</span>
					{!isMobile && airport.iata && (
						<span className={css.terminal_iata}>
							{titleEqualsAirportName ? airport.iata : `(${airport.iata})`}
						</span>
					)}
					{!titleEqualsAirportName && (
						<>
							<span
								className={cn(css.terminal_airport, {
									[css.terminal_airportTerminal]: terminal
								})}
							>
								{airport.title}
							</span>
						</>
					)}

					{terminal && (
						<span className={css.terminal_number}>
							{t('Terminal')} {terminal}
						</span>
					)}
				</span>
			</div>

			<div
				className={cn(css.weather, {
					[css.weather_gradient]: weather,
					[css.weather_placeholder]: showCitiesPlaceholder
				})}
				style={image && !showCitiesPlaceholder ? { backgroundImage: `url(${image})` } : {}}
			>
				{showCitiesPlaceholder && <span className={css.flight_icon}>{flightIcon}</span>}

				{weather && (
					<div className={css.weather_wrapper}>
						{weatherIcon}
						<span className={css.weather_temp}>
							{getTemperaturePrefix(weather.temperature)}
							{Math.abs(Math.round(weather.temperature))}&deg;
							{weather.measurement === TemperatureUnits.Fahrenheit && 'F'}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default Location;
